import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  MaterialSchema,
  MaterialStructureImportSchema,
  MaterialUploadResponseSchema,
  MaterialVolumetricSchema,
} from "../types";

const c = initContract();

export const materialsContract = c.router({
  findAll: {
    method: "GET",
    path: "/materials",
    responses: {
      200: MaterialSchema.pick({
        materialGrade: true,
        name: true,
      }).array(),
    },
    summary: "Find all materials",
  },

  findAllCustomMaterials: {
    method: "GET",
    path: "/materials/custom",
    responses: {
      200: MaterialSchema.array(),
    },
    summary: "Find all custom materials",
  },

  deleteCustomMaterials: {
    method: "DELETE",
    path: "/materials/custom",
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: "Delete all custom materials for the requesting company",
  },

  uploadCustomMaterials: {
    method: "POST",
    path: "/materials/custom",
    contentType: "multipart/form-data",
    body: c.type<{ file: File }>(),
    responses: {
      200: MaterialUploadResponseSchema,
      400: MaterialUploadResponseSchema,
    },
    summary: "Upload custom materials",
  },

  startMaterialStructureImport: {
    method: "POST",
    path: "/materials/structure-imports",
    contentType: "multipart/form-data",
    body: c.type<{ file: File }>(),
    responses: {
      202: z.object({ jobId: z.string().uuid() }),
    },
    summary:
      "Upload a raw material list (CSV or Excel) to be structured by pystruct, replacing the custom materials once done",
  },

  findMaterialStructureImport: {
    method: "GET",
    path: "/materials/structure-imports/:jobId",
    pathParams: z.object({ jobId: z.string().uuid() }),
    responses: {
      200: MaterialStructureImportSchema,
      404: z.object({ message: z.string() }),
    },
    summary: "Get the status and result of a material list import",
  },

  addDefaultsMaterials: {
    method: "POST",
    path: "/materials/add-defaults",
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
  },

  createMaterial: {
    method: "POST",
    path: "/materials",
    body: MaterialSchema.pick({
      name: true,
      type: true,
      subType: true,
      height: true,
      width: true,
      length: true,
      tubeThickness: true,
      density: true,
      materialGrade: true,
      price: true,
    }),
    responses: {
      201: MaterialSchema,
    },
    summary: "Create a single material for the requesting company",
  },
  materialsVolumetricPrices: {
    method: "GET",
    path: "/materials/volumetric-prices",
    responses: {
      200: MaterialVolumetricSchema.pick({
        materialGrade: true,
        volumetricPrice: true,
        id: true,
        name: true,
      }).array(),
    },
    summary: "Get materials volumetric price",
  },
  createMaterialsVolumetricPrice: {
    method: "POST",
    path: "/materials/volumetric-prices",
    body: MaterialVolumetricSchema.pick({
      materialGrade: true,
      volumetricPrice: true,
      name: true,
    }),
    responses: {
      200: MaterialVolumetricSchema,
    },
    summary: "Create materials volumetric price",
  },
  updateMaterialsVolumetricPrice: {
    method: "PUT",
    path: "/materials/volumetric-prices/:id",
    body: MaterialVolumetricSchema.pick({
      materialGrade: true,
      volumetricPrice: true,
      name: true,
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
    },
    summary: "Update materials volumetric price",
  },
  deleteMaterialVolumetricPrice: {
    method: "DELETE",
    path: "/materials/volumetric-prices/:id",
    body: z.object({}).optional(),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
    },
    summary: "Delete materials volumetric price",
  },
  deleteAllVolumetricMaterials: {
    method: "DELETE",
    path: "/materials/volumetric-prices",
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: "Delete all volumetric materials for the requesting company",
  },
  uploadVolumetricBaseMaterials: {
    method: "POST",
    path: "/materials/volumetric-prices/import",
    contentType: "multipart/form-data",
    body: c.type<{ file: File }>(),
    responses: {
      200: MaterialUploadResponseSchema,
      400: MaterialUploadResponseSchema,
    },
    summary: "Upload volumetric base materials from CSV (materialGrade, name, price in cents/kg)",
  },
  updateMaterial: {
    method: "PATCH",
    path: "/materials/:id",
    body: MaterialSchema.pick({
      name: true,
      type: true,
      subType: true,
      height: true,
      width: true,
      length: true,
      tubeThickness: true,
      density: true,
      materialGrade: true,
      price: true,
    }).partial(),
    responses: {
      200: MaterialSchema,
    },
    summary: "Update a single material for the requesting company",
  },
  deleteMaterial: {
    method: "DELETE",
    path: "/materials/:id",
    body: z.object({}).optional(),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: "Delete a single material for the requesting company",
  },
  bulkDeleteMaterials: {
    method: "DELETE",
    path: "/materials/bulk/delete",
    body: z.object({ ids: z.string().uuid().array() }),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: "Delete multiple materials for the requesting company",
  },
  bulkDeleteMaterialVolumetricPrices: {
    method: "DELETE",
    path: "/materials/volumetric-prices/bulk/delete",
    body: z.object({ ids: z.string().uuid().array() }),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: "Delete multiple volumetric materials for the requesting company",
  },
  getMaterialsValidation: {
    method: "GET",
    path: "/materials/validation",
    responses: {
      200: z.object({ duplicateIds: z.string().array() }),
    },
    summary:
      "Get the validation result (duplicated ids) for the requesting company's material base",
  },
});
